import { useCallback, useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { Button } from '@/components/ui/button.tsx';
import { Input } from '@/components/ui/input.tsx';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '@/components/ui/card.tsx';
import { cn } from '@/utils/utils.ts';

type ConnectionStatus = 'disconnected' | 'connecting' | 'connected';

const NORDPOOL_CHANNELS = [
  'nordpool/ticker',
  'nordpool/contracts',
  'nordpool/publicStatistics/111',
  'nordpool/localview/111',
] as const;

type Message = {
  id: string;
  type: 'sent' | 'received';
  data: string;
  timestamp: Date;
};

const WS_TARGET = import.meta.env.VITE_WEBSOCKET_URL;

const normalizeWebSocketUrl = (url: string): string => {
  if (url.startsWith('wss://') || url.startsWith('ws://')) {
    return url;
  }

  const isSecure = window.location.protocol === 'https:';
  const protocol = isSecure ? 'wss://' : 'ws://';

  return `${protocol}${url}`;
};

const getWebSocketUrl = (): string | null => {
  if (import.meta.env.DEV) {
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${protocol}//${window.location.host}/ws`;
  }

  if (!WS_TARGET) {
    return null;
  }

  return normalizeWebSocketUrl(WS_TARGET);
};

const getCloseCodeDescription = (code: number): string => {
  const codes: Record<number, string> = {
    1000: 'Normal closure',
    1001: 'Going away',
    1002: 'Protocol error',
    1003: 'Unsupported data',
    1005: 'No status received',
    1006: 'Abnormal closure',
    1007: 'Invalid payload',
    1008: 'Policy violation',
    1009: 'Message too big',
    1010: 'Missing extension',
    1011: 'Internal error',
    1012: 'Service restart',
    1013: 'Try again later',
    1014: 'Bad gateway',
    1015: 'TLS handshake failed',
    3000: 'Unauthorized',
  };
  return codes[code] || 'Unknown';
};

const loader = document.querySelector('.loader-container')!;
const hideLoader = () => loader.classList.add('loader--hide');

const WebSocketDemoPage = () => {
  const [status, setStatus] = useState<ConnectionStatus>('disconnected');
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [wsUrl] = useState(getWebSocketUrl);
  const [nordPoolSubscriptions, setNordPoolSubscriptions] = useState<string[]>([]);
  const wsRef = useRef<WebSocket | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    hideLoader();
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    return () => {
      wsRef.current?.close();
    };
  }, []);

  const connect = useCallback(() => {
    if (!wsUrl || wsRef.current?.readyState === WebSocket.OPEN) return;

    setStatus('connecting');
    const ws = new WebSocket(wsUrl);

    ws.onopen = () => {
      setStatus('connected');
      setMessages((prev) => [
        ...prev,
        {
          id: crypto.randomUUID(),
          type: 'received',
          data: '🔗 Connected to WebSocket server',
          timestamp: new Date(),
        },
      ]);
    };

    ws.onmessage = (event) => {
      const rawData = typeof event.data === 'string' ? event.data : JSON.stringify(event.data);

      // Try to parse and track Nord Pool subscription state
      try {
        const parsed = JSON.parse(rawData);
        if (parsed.action === 'subscribed' && Array.isArray(parsed.subscriptions)) {
          setNordPoolSubscriptions(parsed.subscriptions);
        } else if (
          parsed.action === 'unsubscribed' &&
          Array.isArray(parsed.subscriptions)
        ) {
          setNordPoolSubscriptions(parsed.subscriptions);
        }
      } catch {
        // Not JSON, ignore
      }

      setMessages((prev) => [
        ...prev,
        {
          id: crypto.randomUUID(),
          type: 'received',
          data: rawData,
          timestamp: new Date(),
        },
      ]);
    };

    ws.onclose = (event) => {
      setStatus('disconnected');
      const reason = event.reason || 'No reason provided';
      const codeInfo = getCloseCodeDescription(event.code);
      setMessages((prev) => [
        ...prev,
        {
          id: crypto.randomUUID(),
          type: 'received',
          data: `🔌 Disconnected from WebSocket server\nCode: ${event.code} (${codeInfo})\nReason: ${reason}`,
          timestamp: new Date(),
        },
      ]);
    };

    ws.onerror = () => {
      setMessages((prev) => [
        ...prev,
        {
          id: crypto.randomUUID(),
          type: 'received',
          data: '❌ WebSocket error occurred (check console for details)',
          timestamp: new Date(),
        },
      ]);
    };

    wsRef.current = ws;
  }, [wsUrl]);

  const disconnect = useCallback(() => {
    wsRef.current?.close();
    wsRef.current = null;
    setNordPoolSubscriptions([]);
  }, []);

  const subscribeNordPool = useCallback((channels: string[]) => {
    if (wsRef.current?.readyState !== WebSocket.OPEN) return;
    const message = JSON.stringify({ action: 'subscribe', channels });
    wsRef.current.send(message);
    setMessages((prev) => [
      ...prev,
      {
        id: crypto.randomUUID(),
        type: 'sent',
        data: message,
        timestamp: new Date(),
      },
    ]);
  }, []);

  const unsubscribeNordPool = useCallback((channels: string[]) => {
    if (wsRef.current?.readyState !== WebSocket.OPEN) return;
    const message = JSON.stringify({ action: 'unsubscribe', channels });
    wsRef.current.send(message);
    setMessages((prev) => [
      ...prev,
      {
        id: crypto.randomUUID(),
        type: 'sent',
        data: message,
        timestamp: new Date(),
      },
    ]);
  }, []);

  const sendMessage = useCallback(() => {
    if (!inputValue.trim() || wsRef.current?.readyState !== WebSocket.OPEN) return;

    wsRef.current.send(inputValue);
    setMessages((prev) => [
      ...prev,
      {
        id: crypto.randomUUID(),
        type: 'sent',
        data: inputValue,
        timestamp: new Date(),
      },
    ]);
    setInputValue('');
  }, [inputValue]);

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      sendMessage();
    }
  };

  const clearMessages = () => {
    setMessages([]);
  };

  const statusColors: Record<ConnectionStatus, string> = {
    disconnected: 'bg-red-500',
    connecting: 'bg-yellow-500',
    connected: 'bg-green-500',
  };

  const statusLabels: Record<ConnectionStatus, string> = {
    disconnected: 'Disconnected',
    connecting: 'Connecting...',
    connected: 'Connected',
  };

  if (!wsUrl) {
    return (
      <div className="flex min-h-svh flex-col gap-6 p-6 sm:p-8">
        <header>
          <h1 className="text-3xl font-bold text-gray-900">WebSocket Demo</h1>
        </header>
        <Card className="border-red-200 bg-red-50">
          <CardHeader>
            <CardTitle className="text-red-900">Configuration Missing</CardTitle>
            <CardDescription className="text-red-700">
              WebSocket URL is not configured
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-red-800">
              The{' '}
              <code className="rounded bg-red-100 px-1.5 py-0.5 font-mono text-xs">
                VITE_WEBSOCKET_URL
              </code>{' '}
              environment variable is not set.
            </p>
            <p className="text-sm text-red-800">
              Please add it to your{' '}
              <code className="rounded bg-red-100 px-1.5 py-0.5 font-mono text-xs">.env</code>{' '}
              file:
            </p>
            <pre className="rounded-lg bg-red-900 p-3 text-xs text-red-50">
              VITE_WEBSOCKET_URL=wss://ws.example.com
            </pre>
            <p className="text-sm text-red-800">
              Note: You can also use a hostname without protocol (e.g.,{' '}
              <code className="rounded bg-red-100 px-1.5 py-0.5 font-mono text-xs">
                ws.example.com
              </code>
              ), and the protocol will be automatically determined based on your site's
              security (wss:// for HTTPS, ws:// for HTTP).
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex min-h-svh flex-col gap-6 p-6 sm:p-8">
      <header>
        <h1 className="text-3xl font-bold text-gray-900">WebSocket Demo</h1>
        <p className="text-muted-foreground mt-1">
          Connect to <code className="rounded bg-gray-100 px-1.5 py-0.5 text-sm">{wsUrl}</code>
          {import.meta.env.DEV && WS_TARGET && (
            <span className="ml-2 text-xs text-amber-600">(proxied to {WS_TARGET})</span>
          )}
        </p>
      </header>

      <div className="grid gap-6 lg:grid-cols-[300px_minmax(0,1fr)]">
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Connection</CardTitle>
              <CardDescription>Manage your WebSocket connection</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-3">
                <div className={cn('h-3 w-3 rounded-full', statusColors[status])} />
                <span className="text-sm font-medium">{statusLabels[status]}</span>
              </div>

              <div className="flex gap-2">
                <Button
                  onClick={connect}
                  disabled={status !== 'disconnected'}
                  className="flex-1"
                >
                  Connect
                </Button>
                <Button
                  onClick={disconnect}
                  disabled={status === 'disconnected'}
                  variant="destructive"
                  className="flex-1"
                >
                  Disconnect
                </Button>
              </div>

              <Button onClick={clearMessages} variant="outline" className="w-full">
                Clear Messages
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Nord Pool Subscriptions</CardTitle>
              <CardDescription>Subscribe to real-time Nord Pool data</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex flex-wrap gap-2">
                <Button
                  onClick={() => subscribeNordPool([...NORDPOOL_CHANNELS])}
                  disabled={status !== 'connected'}
                  size="sm"
                >
                  Subscribe All
                </Button>
                <Button
                  onClick={() => unsubscribeNordPool([...NORDPOOL_CHANNELS])}
                  disabled={status !== 'connected'}
                  variant="outline"
                  size="sm"
                >
                  Unsubscribe All
                </Button>
              </div>

              <div className="space-y-2">
                {NORDPOOL_CHANNELS.map((channel) => {
                  const isSubscribed = nordPoolSubscriptions.includes(channel);
                  return (
                    <div key={channel} className="flex items-center justify-between gap-2">
                      <code className="flex-1 truncate text-xs">{channel}</code>
                      <Button
                        onClick={() =>
                          isSubscribed
                            ? unsubscribeNordPool([channel])
                            : subscribeNordPool([channel])
                        }
                        disabled={status !== 'connected'}
                        variant={isSubscribed ? 'destructive' : 'secondary'}
                        size="sm"
                      >
                        {isSubscribed ? 'Unsub' : 'Sub'}
                      </Button>
                    </div>
                  );
                })}
              </div>

              {nordPoolSubscriptions.length > 0 && (
                <div className="text-muted-foreground text-xs">
                  Active: {nordPoolSubscriptions.length} subscription(s)
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        <Card className="flex h-[800px] flex-col">
          <CardHeader>
            <CardTitle>Messages</CardTitle>
            <CardDescription>
              {messages.length} message{messages.length !== 1 ? 's' : ''}
            </CardDescription>
          </CardHeader>
          <CardContent className="flex min-h-0 flex-1 flex-col gap-4">
            <div className="min-h-0 flex-1 overflow-auto rounded-lg border bg-gray-50 p-4">
              {messages.length === 0 ? (
                <p className="text-muted-foreground text-center text-sm">
                  No messages yet. Connect to the WebSocket server to start.
                </p>
              ) : (
                <div className="space-y-3">
                  {messages.map((msg) => (
                    <div
                      key={msg.id}
                      className={cn(
                        'max-w-[80%] rounded-lg px-3 py-2 text-sm',
                        msg.type === 'sent'
                          ? 'bg-primary text-primary-foreground ml-auto'
                          : 'border bg-white shadow-sm'
                      )}
                    >
                      <pre className="font-mono text-xs break-words whitespace-pre-wrap">
                        {msg.data}
                      </pre>
                      <time
                        className={cn(
                          'mt-1 block text-[10px]',
                          msg.type === 'sent'
                            ? 'text-primary-foreground/70'
                            : 'text-muted-foreground'
                        )}
                      >
                        {msg.timestamp.toLocaleTimeString()}
                      </time>
                    </div>
                  ))}
                  <div ref={messagesEndRef} />
                </div>
              )}
            </div>

            <div className="flex gap-2">
              <Input
                placeholder="Type a message..."
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={handleKeyDown}
                disabled={status !== 'connected'}
              />
              <Button
                onClick={sendMessage}
                disabled={status !== 'connected' || !inputValue.trim()}
              >
                Send
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default WebSocketDemoPage;
